import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../Common/Configurations/AuthContext";
import { useNavigate, Link } from "react-router-dom";
import apiService from "../../Common/Configurations/APIService";
import config from "../../Common/Configurations/APIConfig";
import Header from "../../Common/Layout/Header";
import AgentTabs from "../Agents/AgentTabs";
import { format } from "date-fns";
import Modal from "react-bootstrap/Modal";
import currencyFlags from "../../Data/CurrencyFlags.json";
import DeleteAlert from "../../Common/Layout/DeleteAlert";
import { toast } from "react-toastify"; 

const CompanyAgents = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [agents, setAgents] = useState([]);
  const [filteredAgents, setFilteredAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);

  // Search and filter states
  const [searchTerm, setSearchTerm] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState([]);

  // Filter states
  const [planTypeFilter, setPlanTypeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  // Edit/Add modal states
  const [editShow, setEditShow] = useState(false);
  const [editAgent, setEditAgent] = useState({});
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [editBtnLoading, setEditBtnLoading] = useState(false);

  // Validation states - ADDED
  const [formErrors, setFormErrors] = useState({}); // State for modal form validation

  // Delete modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

   const [showPasswordModal, setShowPasswordModal] = useState(false);
    const [agentToChangePassword, setAgentToChangePassword] = useState(null);
    const [passwordData, setPasswordData] = useState({ newPassword: "", confirmPassword: "" });
    const [passwordErrors, setPasswordErrors] = useState({});
    const [passwordBtnLoading, setPasswordBtnLoading] = useState(false);
  // Countries and currencies state
  const [countries, setCountries] = useState([]);
  const [currencies, setCurrencies] = useState([]);

  // Process countries data from currencyFlags.json
  useEffect(() => {
    if (currencyFlags && Array.isArray(currencyFlags)) {
      const sortedCountries = [...currencyFlags].sort((a, b) =>
        a.country.localeCompare(b.country)
      );
      setCountries(sortedCountries);

      // Extract unique currencies
      const uniqueCurrencies = [];
      const currencyMap = new Map();

      sortedCountries.forEach((country) => {
        if (country.code && !currencyMap.has(country.code)) {
          currencyMap.set(country.code, true);
          uniqueCurrencies.push({
            code: country.code,
            name: country.currency || "",
          });
        }
      });

      // Sort currencies alphabetically by code
      uniqueCurrencies.sort((a, b) => a.code.localeCompare(b.code));
      setCurrencies(uniqueCurrencies);
    }
  }, []);

  // Get agents data
  const GetData = async (currentPage = 1) => {
    if (!user) return;
    setLoading(true);
    try {
      // Build query parameters for AGENT type
      let apiUrl = `${config.GETCOMPANIES}?companyType=AGENT&PageNumber=${currentPage}&PageSize=${pageSize}`;

      // Add filters if provided
      if (planTypeFilter) {
        apiUrl += `&planType=${planTypeFilter}`;
      }
      if (statusFilter !== "") {
        apiUrl += `&isActive=${statusFilter}`;
      }

      const response = await apiService.getWithParams(apiUrl);

      const agentsData = response.data || [];
      setAgents(agentsData);
      setFilteredAgents(agentsData);
      setTotalPages(response.totalPages || 0);
      setTotalRecords(response.totalRecords || 0);
      setPageNumber(response.pageNumber || 1);
      setPageSize(response.pageSize || 20);
    } catch (error) {
      setAgents([]);
      setFilteredAgents([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!authLoading && user) {
      GetData(pageNumber);
    }
  }, [user, authLoading, pageNumber]);

  // Handle filter changes
  const handlePlanTypeFilter = (e) => {
    const value = e.target.value;
    setPlanTypeFilter(value);
    setPageNumber(1);
    GetData(1);
  };

  const handleStatusFilter = (e) => {
    const value = e.target.value;
    setStatusFilter(value);
    setPageNumber(1);
    GetData(1);
  };

  // Reset all filters
  const handleResetFilters = () => {
    setPlanTypeFilter("");
    setStatusFilter("");
    setSearchTerm("");
    setPageNumber(1);
    GetData(1);
  };

  // Generate search suggestions
  const generateSuggestions = (searchValue) => {
    if (!searchValue.trim()) {
      setSuggestions([]);
      return;
    }

    const searchLower = searchValue.toLowerCase();
    const companyMatches = agents
      .filter((agent) => agent.companyName?.toLowerCase().includes(searchLower))
      .slice(0, 5);

    const brandMatches = agents
      .filter(
        (agent) =>
          agent.brandName?.toLowerCase().includes(searchLower) &&
          !agent.companyName?.toLowerCase().includes(searchLower)
      )
      .slice(0, 3);

    const emailMatches = agents
      .filter(
        (agent) =>
          agent.email?.toLowerCase().includes(searchLower) &&
          !agent.companyName?.toLowerCase().includes(searchLower) &&
          !agent.brandName?.toLowerCase().includes(searchLower)
      )
      .slice(0, 2);

    const allSuggestions = [
      ...companyMatches,
      ...brandMatches,
      ...emailMatches,
    ];
    setSuggestions(allSuggestions);
  };

  // Handle search
  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchTerm(value);

    // Generate suggestions
    generateSuggestions(value);
    setShowSuggestions(true);

    if (value.trim() === "") {
      setFilteredAgents(agents);
      setSuggestions([]);
      setShowSuggestions(false);
    } else {
      const filtered = agents.filter(
        (agent) =>
          agent.companyName?.toLowerCase().includes(value.toLowerCase()) ||
          agent.brandName?.toLowerCase().includes(value.toLowerCase()) ||
          agent.email?.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredAgents(filtered);
    }
  };

  // Handle suggestion click
  const handleSuggestionClick = (agent) => {
    setSearchTerm(agent.companyName);
    setFilteredAgents([agent]);
    setShowSuggestions(false);
    setSuggestions([]);
  };

  // Handle input blur (hide suggestions after a delay)
  const handleInputBlur = () => {
    setTimeout(() => {
      setShowSuggestions(false);
    }, 200);
  };
   const handleOpenPasswordModal = (agent) => {
        setAgentToChangePassword(agent);
        setShowPasswordModal(true);
    };

    // Close the password change modal and reset states
    const handleClosePasswordModal = () => {
        setShowPasswordModal(false);
        setAgentToChangePassword(null);
        setPasswordData({ newPassword: "", confirmPassword: "" });
        setPasswordErrors({});
        setPasswordBtnLoading(false);
    };

    // Handle input change in the password modal
    const handlePasswordInputChange = (e) => {
        const { name, value } = e.target;
        setPasswordData({ ...passwordData, [name]: value });
        // Clear error as user types
        if (passwordErrors[name]) {
            setPasswordErrors({ ...passwordErrors, [name]: null });
        }
    };
     const validatePasswordForm = () => {
        const errors = {};
        if (!passwordData.newPassword || passwordData.newPassword.length < 6) {
            errors.newPassword = "Password must be at least 6 characters long.";
        }
        if (passwordData.newPassword !== passwordData.confirmPassword) {
            errors.confirmPassword = "Passwords do not match.";
        }
        setPasswordErrors(errors);
        return Object.keys(errors).length === 0;
    };

    // Handle the submission of the new password
    const handleChangePassword = async () => {
        if (validatePasswordForm()) {
            setPasswordBtnLoading(true);
            try {
                const payload = {
                    UserId: agentToChangePassword.userId, // IMPORTANT: Ensure your agent object has userId
                    NewPassword: passwordData.newPassword,
                };

                // IMPORTANT: Assuming you have an endpoint for an admin to change a user's password.
                // The 'CHANGEPASSWORD' endpoint you provided might require an OldPassword, which an admin won't have.
                // You might need a different endpoint, e.g., config.ADMIN_CHANGE_PASSWORD
                const res = await apiService.post(config.CHANGEPASSWORD, payload); // Or config.ADMIN_CHANGE_PASSWORD

                if (res === true) {
                    toast.success(`Password for ${agentToChangePassword.companyName} changed successfully!`);
                    handleClosePasswordModal();
                } else {
                    toast.error("Failed to change password. The user might not exist or there was a server error.");
                }
            } catch (error) {
                toast.error("An error occurred. Please try again.");
            } finally {
                setPasswordBtnLoading(false);
            }
        }
    };


  // Pagination functions
  const GetFirstPageData = () => {
    GetData(1);
  };

  const GetLastPageData = () => {
    GetData(totalPages);
  };

  const GetPageData = (number) => {
    setPageNumber(number);
    if (pageNumber !== number) GetData(number);
  };

  const pageNumbers = [];
  for (let i = 1; i <= totalPages; i++) {
    pageNumbers.push(i);
  }

  const renderPageNumbers = pageNumbers.map((number) => {
    return (
      <li
        className={`page-item ${pageNumber === number ? "active" : ""}`}
        key={number}
        onClick={() => GetPageData(number)}
      >
        <Link className="page-link">{number}</Link>
      </li>
    );
  });

  // Handle Add Agent button click
  const handleAddAgent = () => {
    const newAgent = {
      companyId: "",
      companyName: "",
      brandName: "",
      email: "",
      phoneNumber: "",
      gstNo: "",
      panCard: "",
      address: "",
      city: "",
      state: "",
      country: "IN",
      currency: "INR",
      primaryDomain: "",
      isActive: true,
      planType: "CUSTOM",
      companyType: "AGENT",
      connects: 0,
      isWebsite: false,
      isRenewal: false,
      endDate: new Date(),
      whatsAppNumber: "",
      whatsAppPhoneNumberId: "",
      whatsAppPermanentToken: "",
      isChatBot: false,
      isSupplierAPI: false,
    };

    setEditAgent(newAgent);
    setLogoPreview(null);
    setLogoFile(null);
    setFormErrors({}); // Reset validation errors
    setEditShow(true);
  };

  // Handle edit icon click
  const handleEditClick = (agent) => {
    const cleanedData = {};
    Object.keys(agent).forEach((key) => {
      if (
        agent[key] === null ||
        agent[key] === "null" ||
        agent[key] === "undefined"
      ) {
        cleanedData[key] = "";
      } else {
        cleanedData[key] = agent[key];
      }
    });

    setEditAgent(cleanedData);
    setLogoPreview(cleanedData.logoUrl || cleanedData.logoSrc);
    setFormErrors({}); // Reset validation errors
    setEditShow(true);
  };

  // Close edit modal
  const handleEditClose = () => {
    setEditShow(false);
    setEditAgent({});
    setLogoFile(null);
    setLogoPreview(null);
    setFormErrors({}); // Reset validation errors
  };

  // Handle file change for logo
  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setLogoFile(file);
      setLogoPreview(URL.createObjectURL(file));
    }
  };

  // Handle country change
  const handleCountryChange = (countryCode) => {
    const selectedCountry = countries.find(
      (country) => country.countryCode === countryCode
    );
    setEditAgent({
      ...editAgent,
      country: countryCode,
      currency: selectedCountry?.code || "",
    });
  };

  // Handle currency change
  const handleCurrencyChange = (currencyCode) => {
    setEditAgent({
      ...editAgent,
      currency: currencyCode,
    });
  };

  // Check if country is India
  const isIndia = (countryCode) => {
    return countryCode === "IN";
  };

  // Handle input change for edit form - UPDATED WITH VALIDATION
  const handleEditInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const fieldsToUppercase = [
      "companyName",
      "brandName",
      "city",
      "state",
      "gstNo",
      "panCard",
    ];

    setEditAgent({
      ...editAgent,
      [name]:
        type === "checkbox"
          ? checked
          : fieldsToUppercase.includes(name)
          ? value.toUpperCase()
          : value,
    });

    // Clear the specific error when user starts typing - ADDED
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: null,
      });
    }
  };

  // VALIDATION LOGIC - ADDED
  const validateAgentForm = () => {
    const newErrors = {};

    // Company Name validation
    if (!editAgent.companyName || editAgent.companyName.trim() === "") {
      newErrors.companyName = "Company Name is required.";
    }

    // Brand Name validation
    if (!editAgent.brandName || editAgent.brandName.trim() === "") {
      newErrors.brandName = "Brand Name is required.";
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!editAgent.email || !emailRegex.test(editAgent.email)) {
      newErrors.email = "Please enter a valid email address.";
    }

    // Phone Number validation
    const phoneRegex = /^\d{10,15}$/;
    if (!editAgent.phoneNumber || !phoneRegex.test(editAgent.phoneNumber)) {
      newErrors.phoneNumber =
        "Please enter a valid phone number (10-15 digits).";
    }

    setFormErrors(newErrors);
    // Return true if there are no errors
    return Object.keys(newErrors).length === 0;
  };

  // Handle update agent - UPDATED WITH VALIDATION
  const handleUpdateAgent = async (e) => {
    e.preventDefault(); // ADDED preventDefault
    if (validateAgentForm()) {
      // ADDED VALIDATION CHECK
      setEditBtnLoading(true);

      try {
        if (!editAgent.companyId) {
          const newAgentData = {
            companyName: editAgent.companyName || "",
            brandName: editAgent.brandName || "",
            email: editAgent.email || "",
            phoneNumber: editAgent.phoneNumber || "",
            gstNo: editAgent.gstNo || "",
            panCard: editAgent.panCard || "",
            address: editAgent.address || "",
            city: editAgent.city || "",
            state: editAgent.state || "",
            country: editAgent.country || "",
            currency: editAgent.currency || "",
            companyType: "AGENT",
            parentCompanyId: user?.cid,
            isActive: editAgent.isActive,
            planType: "CUSTOM",
            primaryDomain: editAgent.primaryDomain || "",
            connects: editAgent.connects || 0,
            isWebsite: editAgent.isWebsite || false,
            isRenewal: editAgent.isRenewal || false,
            endDate: editAgent.endDate || new Date(),
            whatsAppNumber: editAgent.whatsAppNumber || "",
            whatsAppPhoneNumberId: editAgent.whatsAppPhoneNumberId || "",
            whatsAppPermanentToken: editAgent.whatsAppPermanentToken || "",
            isChatBot: editAgent.isChatBot || false,
            isSupplierAPI: editAgent.isSupplierAPI || false,
            commissionRate: editAgent.commissionRate || 10,
            userType: "AGENT",
            password: "App@000",
          };

          const response = await apiService.create(
            config.CREATECOMPANYBYSUPERADMIN,
            newAgentData
          );

          if (response && response.success) {
            await GetData(pageNumber);
            handleEditClose();
          } else {
          }
        } else {
          const formData = new FormData();

          formData.append("companyId", editAgent.companyId);
          formData.append("companyName", editAgent.companyName || "");
          formData.append("brandName", editAgent.brandName || "");
          formData.append("email", editAgent.email || "");
          formData.append("phoneNumber", editAgent.phoneNumber || "");
          formData.append("gstNo", editAgent.gstNo || "");
          formData.append("panCard", editAgent.panCard || "");
          formData.append("address", editAgent.address || "");
          formData.append("city", editAgent.city || "");
          formData.append("state", editAgent.state || "");
          formData.append("country", editAgent.country || "");
          formData.append("currency", editAgent.currency || "");
          formData.append("primaryDomain", editAgent.primaryDomain || "");
          formData.append("isActive", editAgent.isActive);
          formData.append("planType", editAgent.planType || "");
          formData.append("connects", editAgent.connects || 0);
          formData.append("isWebsite", editAgent.isWebsite || false);
          formData.append("isRenewal", editAgent.isRenewal || false);
          formData.append("endDate", editAgent.endDate || "");
          formData.append("whatsAppNumber", editAgent.whatsAppNumber || "");
          formData.append(
            "whatsAppPhoneNumberId",
            editAgent.whatsAppPhoneNumberId || ""
          );
          formData.append(
            "whatsAppPermanentToken",
            editAgent.whatsAppPermanentToken || ""
          );
          formData.append("isChatBot", editAgent.isChatBot || false);
          formData.append("isSupplierAPI", editAgent.isSupplierAPI || false);
          formData.append("commissionRate", editAgent.commissionRate || 10);

          if (logoFile) {
            formData.append("logoFile", logoFile);
          }

          await apiService.update(config.UPDATECOMPANYBYSUPERADMIN, formData);
          await GetData(pageNumber);
          handleEditClose();
        }
      } catch (error) {
      } finally {
        setEditBtnLoading(false);
      }
    }
  };

  // Delete functionality
  const handleOpenDeleteModal = (agentId) => {
    setItemToDelete(agentId);
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setItemToDelete(null);
  };

  const handleDelete = async () => {
    if (itemToDelete !== null) {
      try {
        await apiService.remove(config.DELETECOMPANY, itemToDelete);
        await GetData(pageNumber);
      } catch (error) {}
      handleCloseDeleteModal();
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return format(new Date(dateString), "dd MMM, yyyy hh:mm a");
  };

  if (authLoading) return <p>Loading...</p>;
  if (!user) {
    navigate("/");
    return null;
  }

  return (
    <>
      <Header />
      <div className="main-content">
        <div className="page-content">
          <div className="container-fluid">
            <div className="card">
              <div className="p-2">
                <AgentTabs pageName="AGENTS" />
              </div>

              <div className="card-body">
                <div className="row mb-4">
                  <div className="col-12">
                    <div className="d-flex justify-content-between align-items-center mb-4">
                      <h5 className="card-title mb-0">Agent Companies</h5>
                      <button
                        className="btn btn-primary"
                        onClick={handleAddAgent}
                      >
                        <i className="ri-add-line me-2"></i>
                        Add Agent
                      </button>
                    </div>

                    {/* Filter Section */}
                    <div
                      className="row align-items-end mb-4"
                      style={{ gap: "8px 0" }}
                    >
                      {/* Search Bar - wider column */}
                      <div className="col-md-6">
                        <div style={{ position: "relative" }}>
                          <div style={{ position: "relative" }}>
                            <i
                              className="ri-search-line"
                              style={{
                                position: "absolute",
                                left: "12px",
                                top: "50%",
                                transform: "translateY(-50%)",
                                zIndex: 10,
                                color: "#6c757d",
                              }}
                            ></i>
                            <input
                              type="text"
                              className="form-control"
                              placeholder="Search by company name, brand name, or email..."
                              value={searchTerm}
                              onChange={handleSearch}
                              onFocus={() => setShowSuggestions(true)}
                              onBlur={handleInputBlur}
                              style={{
                                height: "45px",
                                paddingLeft: "40px",
                                position: "relative",
                              }}
                            />
                            {searchTerm && (
                              <button
                                className="btn position-absolute end-0 top-0 bottom-0"
                                style={{
                                  background: "transparent",
                                  border: "none",
                                  zIndex: 10,
                                }}
                                onClick={() => setSearchTerm("")}
                              >
                                <i className="ri-close-line fs-5"></i>
                              </button>
                            )}

                            {/* Suggestions dropdown */}
                            {showSuggestions && suggestions.length > 0 && (
                              <div
                                style={{
                                  position: "absolute",
                                  width: "100%",
                                  marginTop: "4px",
                                  border: "1px solid #dee2e6",
                                  backgroundColor: "white",
                                  borderRadius: "6px",
                                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                                  zIndex: 1000,
                                }}
                              >
                                {suggestions.map((agent) => (
                                  <div
                                    key={agent.companyId}
                                    style={{
                                      padding: "8px 12px",
                                      display: "flex",
                                      alignItems: "center",
                                      cursor: "pointer",
                                      backgroundColor: "white",
                                      transition: "background-color 0.2s",
                                    }}
                                    onMouseEnter={(e) => {
                                      e.target.style.backgroundColor =
                                        "#f8f9fa";
                                    }}
                                    onMouseLeave={(e) => {
                                      e.target.style.backgroundColor = "white";
                                    }}
                                    onClick={() => handleSuggestionClick(agent)}
                                  >
                                    <i className="ri-user-shared-line me-2 text-secondary"></i>
                                    <div>
                                      <div style={{ fontWeight: "600" }}>
                                        {agent.companyName}
                                      </div>
                                      {agent.brandName &&
                                        agent.brandName !==
                                          agent.companyName && (
                                          <div
                                            style={{
                                              fontSize: "0.875rem",
                                              color: "#6c757d",
                                            }}
                                          >
                                            {agent.brandName}
                                          </div>
                                        )}
                                      {agent.email && (
                                        <div
                                          style={{
                                            fontSize: "0.875rem",
                                            color: "#6c757d",
                                          }}
                                        >
                                          {agent.email}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Plan Type Filter */}
                      <div className="col-md-2">
                        <label className="form-label">Plan Type</label>
                        <select
                          className="form-select"
                          value={planTypeFilter}
                          onChange={handlePlanTypeFilter}
                          style={{ height: "45px" }}
                        >
                          <option value="">All Plans</option>
                          <option value="TRIAL">TRIAL</option>
                          <option value="MONTHLYSTARTER">MONTHLYSTARTER</option>
                          <option value="MONTHLYPRO">MONTHLYPRO</option>
                          <option value="MONTHLYENTERPRISE">
                            MONTHLYENTERPRISE
                          </option>
                          <option value="CUSTOM">CUSTOM</option>
                        </select>
                      </div>

                      {/* Status Filter */}
                      <div className="col-md-2">
                        <label className="form-label">Status</label>
                        <select
                          className="form-select"
                          value={statusFilter}
                          onChange={handleStatusFilter}
                          style={{ height: "45px" }}
                        >
                          <option value="">All Status</option>
                          <option value="true">Active</option>
                          <option value="false">Inactive</option>
                        </select>
                      </div>

                      {/* Reset Button */}
                      <div className="col-md-1">
                        <button
                          className="btn btn-danger w-100"
                          onClick={handleResetFilters}
                          style={{ height: "45px" }}
                        >
                          <i className="ri-refresh-line me-1"></i>
                          Reset
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Table Section with proper gap */}
                <div className="mt-4">
                  <div className="table-responsive table-card">
                    <table className="table align-middle table-hover">
                      <thead className="table-light">
                        <tr>
                          <th>S.No</th>
                          <th>Company / Brand</th>
                          <th>Contact</th>
                          <th>Plan</th>

                          <th>Connects Left</th>
                          <th>Expires On</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {loading ? (
                          <tr>
                            <td colSpan="9" className="text-center py-4">
                              Loading agents...
                            </td>
                          </tr>
                        ) : filteredAgents.length > 0 ? (
                          filteredAgents.map((agent, index) => (
                            <tr key={agent.companyId}>
                              <td>{index + 1}</td>
                              <td>
                                <div>
                                  <strong>{agent.companyName}</strong>
                                </div>
                                <small className="text-muted">
                                  {agent.brandName || "-"}
                                </small>
                              </td>
                              <td>
                                <div>{agent.email}</div>
                                <small className="text-muted">
                                  {agent.phoneNumber}
                                </small>
                              </td>
                              <td>
                                <span className="badge bg-primary">
                                  {agent.planType || "N/A"}
                                </span>
                              </td>

                              <td>{agent.connects}</td>
                              <td>{formatDate(agent.endDate)}</td>
                              <td>
                                <span
                                  className={`badge ${
                                    agent.isActive ? "bg-success" : "bg-danger"
                                  }`}
                                >
                                  {agent.isActive ? "Active" : "Inactive"}
                                </span>
                              </td>
                              <td>
                                <ul className="list-inline hstack gap-2 mb-0">
                                  <li
                                    className="list-inline-item"
                                    data-bs-toggle="tooltip"
                                    data-bs-trigger="hover"
                                    data-bs-placement="top"
                                    title="Edit"
                                  >
                                    <Link
                                      className="edit-item-btn"
                                      onClick={(e) => handleEditClick(agent)}
                                    >
                                      <i className="ri-pencil-fill align-bottom text-muted" />
                                    </Link>
                                  </li>
                                  <li
                                    className="list-inline-item"
                                    data-bs-toggle="tooltip"
                                    data-bs-trigger="hover"
                                    data-bs-placement="top"
                                    title="Delete"
                                  >
                                    <Link
                                      className="remove-item-btn"
                                      onClick={(e) =>
                                        handleOpenDeleteModal(agent.companyId)
                                      }
                                    >
                                      <i className="ri-delete-bin-fill align-bottom text-muted" />
                                    </Link>
                                  </li>
                                </ul>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="9" className="text-center py-4">
                              No agents found.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Pagination section */}
                {filteredAgents.length > 0 && (
                  <div className="align-items-center mt-4 pt-2 justify-content-between d-flex">
                    <div className="flex-shrink-0">
                      <div className="text-muted">
                        Showing{" "}
                        <span className="fw-semibold">
                          {filteredAgents.length}
                        </span>{" "}
                        of <span className="fw-semibold">{totalRecords}</span>{" "}
                        Results
                      </div>
                    </div>
                    <ul className="pagination pagination-separated pagination-sm mb-0">
                      <li className="page-item" onClick={GetFirstPageData}>
                        <Link className="page-link">Previous</Link>
                      </li>
                      {renderPageNumbers}
                      <li className="page-item" onClick={GetLastPageData}>
                        <Link className="page-link">Next</Link>
                      </li>
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Edit/Add Agent Modal - UPDATED WITH VALIDATION */}
        <Modal
          show={editShow}
          onHide={handleEditClose}
          size="lg"
          centered
          scrollable
        >
          <Modal.Header closeButton>
            <Modal.Title>
              {editAgent.companyId
                ? `Edit Agent - ${editAgent.companyName}`
                : "Add New Agent"}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ maxHeight: "70vh", overflowY: "auto" }}>
            <div className="container-fluid">
              {/* Company Logo */}
              <div className="row mb-4">
                <label className="col-sm-3 col-form-label">Company Logo:</label>
                <div className="col-sm-9">
                  <div className="d-flex align-items-center gap-3">
                    {logoPreview ? (
                      <img
                        src={logoPreview}
                        alt="Company Logo"
                        className="img-thumbnail"
                        style={{
                          width: "120px",
                          height: "120px",
                          objectFit: "contain",
                        }}
                        onError={(e) => {
                          e.target.style.display = "none";
                        }}
                      />
                    ) : (
                      <div
                        className="img-thumbnail d-flex align-items-center justify-content-center"
                        style={{
                          width: "120px",
                          height: "120px",
                          backgroundColor: "#f8f9fa",
                          border: "1px solid #dee2e6",
                        }}
                      >
                        <i className="ri-user-shared-line text-muted fs-2"></i>
                      </div>
                    )}
                    {editShow && (
                      <div className="flex-grow-1">
                        <input
                          type="file"
                          className="form-control"
                          accept="image/png, image/jpeg, image/gif"
                          onChange={handleFileChange}
                        />
                        <div className="form-text">
                          Recommended size: 120x120px. Supports PNG, JPG, GIF.
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Company Name - UPDATED WITH VALIDATION */}
              <div className="row mb-3">
                <label className="col-sm-3 col-form-label">Company Name:</label>
                <div className="col-sm-9">
                  <input
                    type="text"
                    className={`form-control ${
                      formErrors.companyName ? "is-invalid" : ""
                    }`}
                    value={editAgent.companyName || ""}
                    maxLength={40}
                    name="companyName"
                    onChange={handleEditInputChange}
                    placeholder="Enter company name"
                  />
                  {formErrors.companyName && (
                    <div className="invalid-feedback">
                      {formErrors.companyName}
                    </div>
                  )}
                </div>
              </div>

              {/* Brand Name - UPDATED WITH VALIDATION */}
              <div className="row mb-3">
                <label className="col-sm-3 col-form-label">Brand Name:</label>
                <div className="col-sm-9">
                  <input
                    type="text"
                    className={`form-control ${
                      formErrors.brandName ? "is-invalid" : ""
                    }`}
                    value={editAgent.brandName || ""}
                    maxLength={40}
                    name="brandName"
                    onChange={handleEditInputChange}
                    placeholder="Enter brand name"
                  />
                  {formErrors.brandName && (
                    <div className="invalid-feedback">
                      {formErrors.brandName}
                    </div>
                  )}
                </div>
              </div>

              {/* Email - UPDATED WITH VALIDATION */}
              <div className="row mb-3">
                <label className="col-sm-3 col-form-label">Email:</label>
                <div className="col-sm-9">
                  <input
                    type="email"
                    className={`form-control ${
                      formErrors.email ? "is-invalid" : ""
                    }`}
                    value={editAgent.email || ""}
                    maxLength={40}
                    name="email"
                    onChange={handleEditInputChange}
                    placeholder="Enter email address"
                  />
                  {formErrors.email && (
                    <div className="invalid-feedback">{formErrors.email}</div>
                  )}
                </div>
              </div>

              {/* Phone - UPDATED WITH VALIDATION */}
              <div className="row mb-3">
                <label className="col-sm-3 col-form-label">Phone:</label>
                <div className="col-sm-9">
                  <input
                    type="text"
                    className={`form-control ${
                      formErrors.phoneNumber ? "is-invalid" : ""
                    }`}
                    value={editAgent.phoneNumber || ""}
                    maxLength={15}
                    name="phoneNumber"
                    onChange={handleEditInputChange}
                    placeholder="Enter phone number"
                  />
                  {formErrors.phoneNumber && (
                    <div className="invalid-feedback">
                      {formErrors.phoneNumber}
                    </div>
                  )}
                </div>
              </div>
              {/* City */}
              <div className="row mb-3">
                <label className="col-sm-3 col-form-label">City:</label>
                <div className="col-sm-9">
                  <input
                    type="text"
                    className="form-control"
                    value={editAgent.city || ""}
                    maxLength={40}
                    name="city"
                    onChange={handleEditInputChange}
                    placeholder="Enter city"
                  />
                </div>
              </div>

              {/* State */}
              <div className="row mb-3">
                <label className="col-sm-3 col-form-label">State:</label>
                <div className="col-sm-9">
                  <input
                    type="text"
                    className="form-control"
                    value={editAgent.state || ""}
                    maxLength={40}
                    name="state"
                    onChange={handleEditInputChange}
                    placeholder="Enter state"
                  />
                </div>
              </div>

              {/* Address */}
              <div className="row mb-3">
                <label className="col-sm-3 col-form-label">Address:</label>
                <div className="col-sm-9">
                  <textarea
                    className="form-control"
                    value={editAgent.address || ""}
                    maxLength={250}
                    name="address"
                    onChange={handleEditInputChange}
                    placeholder="Enter address"
                    rows="3"
                  />
                </div>
              </div>

              {/* Country */}
              <div className="row mb-3">
                <label className="col-sm-3 col-form-label">Country:</label>
                <div className="col-sm-9">
                  <select
                    className="form-select"
                    value={editAgent.country || ""}
                    name="country"
                    onChange={(e) => handleCountryChange(e.target.value)}
                  >
                    <option value="">Select Country</option>
                    {countries.map((country) => (
                      <option
                        key={country.countryCode}
                        value={country.countryCode}
                      >
                        {country.flag && (
                          <img
                            src={country.flag}
                            alt={country.country}
                            style={{
                              width: "20px",
                              height: "15px",
                              marginRight: "8px",
                              verticalAlign: "middle",
                            }}
                          />
                        )}
                        {country.country} - {country.countryCode}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Currency */}
              <div className="row mb-3">
                <label className="col-sm-3 col-form-label">Currency:</label>
                <div className="col-sm-9">
                  <select
                    className="form-select"
                    value={editAgent.currency || ""}
                    onChange={(e) => handleCurrencyChange(e.target.value)}
                  >
                    <option value="">Select Currency</option>
                    {countries.map((country) => (
                      <option key={country.countryCode} value={country.code}>
                        {country.flag && (
                          <img
                            src={country.flag}
                            alt={country.country}
                            style={{
                              width: "20px",
                              height: "15px",
                              marginRight: "8px",
                              verticalAlign: "middle",
                            }}
                          />
                        )}
                        {country.country} - {country.code}
                        {country.currency && ` (${country.currency})`}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              {/* GST No / Tax No - Conditionally rendered based on country */}
              <div className="row mb-3">
                <label className="col-sm-3 col-form-label">
                  {isIndia(editAgent.country) ? "GST No:" : "Tax No:"}
                </label>
                <div className="col-sm-9">
                  <input
                    type="text"
                    className="form-control"
                    value={editAgent.gstNo || ""}
                    maxLength={15}
                    name="gstNo"
                    onChange={handleEditInputChange}
                    placeholder={
                      isIndia(editAgent.country)
                        ? "Enter GST number"
                        : "Enter tax number"
                    }
                  />
                </div>
              </div>

              {/* Pan Card - Only show for India */}
              {isIndia(editAgent.country) && (
                <div className="row mb-3">
                  <label className="col-sm-3 col-form-label">Pan Card:</label>
                  <div className="col-sm-9">
                    <input
                      type="text"
                      className="form-control"
                      value={editAgent.panCard || ""}
                      maxLength={15}
                      name="panCard"
                      onChange={handleEditInputChange}
                      placeholder="Enter PAN card number"
                    />
                  </div>
                </div>
              )}

              {/* Rest of the form fields remain the same */}
              {/* WhatsApp Number */}
              <div className="row mb-3">
                <label className="col-sm-3 col-form-label">
                  WhatsApp Number:
                </label>
                <div className="col-sm-9">
                  <input
                    type="text"
                    className="form-control"
                    value={editAgent.whatsAppNumber || ""}
                    maxLength={15}
                    name="whatsAppNumber"
                    onChange={handleEditInputChange}
                    placeholder="Enter WhatsApp number"
                  />
                </div>
              </div>

              {/* Primary Domain */}
              <div className="row mb-3">
                <label className="col-sm-3 col-form-label">
                  Primary Domain:
                </label>
                <div className="col-sm-9">
                  <input
                    type="text"
                    className="form-control"
                    value={editAgent.primaryDomain || ""}
                    maxLength={100}
                    name="primaryDomain"
                    onChange={handleEditInputChange}
                    placeholder="e.g., https://example.com"
                  />
                </div>
              </div>
              {/* Plan Type */}
              <div className="row mb-3">
                <label className="col-sm-3 col-form-label">Plan Type:</label>
                <div className="col-sm-9">
                  <select
                    className="form-select"
                    value={editAgent.planType || "TRAIL"}
                    name="planType"
                    onChange={handleEditInputChange}
                  >
                    <option value="TRAIL">TRAIL</option>
                    <option value="MONTHLYSTARTER">MONTHLYSTARTER</option>
                    <option value="MONTHLYPRO">MONTHLYPRO</option>
                    <option value="MONTHLYENTERPRISE">MONTHLYENTERPRISE</option>
                    <option value="CUSTOM">CUSTOM</option>
                  </select>
                </div>
              </div>

              {/* Connects */}
              <div className="row mb-3">
                <label className="col-sm-3 col-form-label">Connects:</label>
                <div className="col-sm-9">
                  <input
                    type="number"
                    className="form-control"
                    value={editAgent.connects || 0}
                    name="connects"
                    onChange={handleEditInputChange}
                    placeholder="Enter number of connects"
                  />
                </div>
              </div>

              {/* End Date */}
              <div className="row mb-3">
                <label className="col-sm-3 col-form-label">End Date:</label>
                <div className="col-sm-9">
                  <input
                    type="datetime-local"
                    className="form-control"
                    value={
                      editAgent.endDate
                        ? new Date(editAgent.endDate).toISOString().slice(0, 16)
                        : ""
                    }
                    name="endDate"
                    onChange={handleEditInputChange}
                  />
                </div>
              </div>

              {/* Status */}
              <div className="row mb-3">
                <label className="col-sm-3 col-form-label">Status:</label>
                <div className="col-sm-9">
                  <select
                    className="form-select"
                    value={editAgent.isActive}
                    name="isActive"
                    onChange={handleEditInputChange}
                  >
                    <option value={true}>Active</option>
                    <option value={false}>Inactive</option>
                  </select>
                </div>
              </div>

              {/* Separated Features Section */}
              <div className="row mb-3">
                <div className="col-12">
                  <h6 className="border-bottom pb-2 mb-3">Features</h6>
                </div>
              </div>

              {/* Website Feature */}
              <div className="row mb-3">
                <label className="col-sm-3 col-form-label">Website:</label>
                <div className="col-sm-9">
                  <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      role="switch"
                      checked={editAgent.isWebsite || false}
                      name="isWebsite"
                      onChange={handleEditInputChange}
                    />
                    <label className="form-check-label">
                      Enable Website Feature
                    </label>
                  </div>
                </div>
              </div>

              {/* Chat Bot Feature */}
              <div className="row mb-3">
                <label className="col-sm-3 col-form-label">Chat Bot:</label>
                <div className="col-sm-9">
                  <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      role="switch"
                      checked={editAgent.isChatBot || false}
                      name="isChatBot"
                      onChange={handleEditInputChange}
                    />
                    <label className="form-check-label">
                      Enable Chat Bot Feature
                    </label>
                  </div>
                </div>
              </div>

              {/* Supplier API Feature */}
              <div className="row mb-3">
                <label className="col-sm-3 col-form-label">Supplier API:</label>
                <div className="col-sm-9">
                  <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      role="switch"
                      checked={editAgent.isSupplierAPI || false}
                      name="isSupplierAPI"
                      onChange={handleEditInputChange}
                    />
                    <label className="form-check-label">
                      Enable Supplier API Feature
                    </label>
                  </div>
                </div>
              </div>

              {/* Renewal Feature */}
              <div className="row mb-3">
                <label className="col-sm-3 col-form-label">Renewal:</label>
                <div className="col-sm-9">
                  <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      role="switch"
                      checked={editAgent.isRenewal || false}
                      name="isRenewal"
                      onChange={handleEditInputChange}
                    />
                    <label className="form-check-label">
                      Enable Auto Renewal
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <div className="d-flex justify-content-center w-100">
              {editBtnLoading ? (
                <button className="btn btn-success me-2 px-4" disabled>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  {editAgent.companyId ? "Saving..." : "Creating..."}
                </button>
              ) : (
                <button
                  className="btn btn-success me-2 px-4"
                  onClick={handleUpdateAgent}
                >
                  <i className="fas fa-save me-2"></i>
                  {editAgent.companyId ? "Save Changes" : "Create Agent"}
                </button>
              )}
              <button className="btn btn-danger px-4" onClick={handleEditClose}>
                <i className="fas fa-times me-2"></i>Cancel
              </button>
            </div>
          </Modal.Footer>
        </Modal>
        <Modal show={showPasswordModal} onHide={handleClosePasswordModal} centered>
                    <Modal.Header closeButton>
                        <Modal.Title>Change Password</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <p>You are changing the password for agent: <strong>{agentToChangePassword?.companyName}</strong></p>
                        
                        <div className="mb-3">
                            <label className="form-label">New Password</label>
                            <input
                                type="password"
                                name="newPassword"
                                className={`form-control ${passwordErrors.newPassword ? 'is-invalid' : ''}`}
                                value={passwordData.newPassword}
                                onChange={handlePasswordInputChange}
                                placeholder="Enter new password"
                            />
                            {passwordErrors.newPassword && <div className="invalid-feedback">{passwordErrors.newPassword}</div>}
                        </div>

                        <div className="mb-3">
                            <label className="form-label">Confirm New Password</label>
                            <input
                                type="password"
                                name="confirmPassword"
                                className={`form-control ${passwordErrors.confirmPassword ? 'is-invalid' : ''}`}
                                value={passwordData.confirmPassword}
                                onChange={handlePasswordInputChange}
                                placeholder="Confirm new password"
                            />
                            {passwordErrors.confirmPassword && <div className="invalid-feedback">{passwordErrors.confirmPassword}</div>}
                        </div>
                    </Modal.Body>
                    <Modal.Footer>
                        <button className="btn btn-light" onClick={handleClosePasswordModal}>Cancel</button>
                        <button
                            className="btn btn-primary"
                            onClick={handleChangePassword}
                            disabled={passwordBtnLoading}
                        >
                            {passwordBtnLoading ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                    Saving...
                                </>
                            ) : (
                                "Save Password"
                            )}
                        </button>
                    </Modal.Footer>
                </Modal>


        {/* Delete Confirmation Modal */}
        <DeleteAlert
          show={showDeleteModal}
          handleClose={handleCloseDeleteModal}
          handleDelete={handleDelete}
        />
      </div>
    </>
  );
};

export default CompanyAgents;
